/**
 * Header component for the Grade dashboard
 */

import { Link, useLocation } from 'react-router-dom';
import { UserButton, useUser } from '@clerk/clerk-react';
import { useDashboard } from '../../hooks';
import { useAuthContext } from '../../contexts/AuthContext';

export function Header() {
  const location = useLocation();
  const { user: clerkUser } = useUser();
  const { user: backendUser, isAuthenticated } = useAuthContext();
  const dashboard = useDashboard();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const linkClass = (path: string) => {
    return isActive(path)
      ? 'text-green-600 bg-green-50 px-3 py-2 rounded-md text-sm font-medium'
      : 'text-gray-500 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium';
  };

  return (
    <header className='bg-white border-b border-gray-200'>
      <div className='w-full px-4 sm:px-6 lg:px-8'>
        <div className='flex justify-between items-center h-16'>
          {/* Logo and navigation */}
          <div className='flex items-center'>
            <Link to='/dashboard' className='flex items-center space-x-3'>
              <div className='w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center'>
                <svg
                  className='w-6 h-6 text-white'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
                  />
                </svg>
              </div>
              <div>
                <span className='text-xl font-bold text-gray-900'>Grade</span>
              </div>
            </Link>

            <nav className='hidden md:flex space-x-8 ml-10'>
              <Link to='/dashboard' className={linkClass('/dashboard')}>
                Dashboard
              </Link>
              <Link to='/evaluations' className={linkClass('/evaluations')}>
                Evaluaciones
              </Link>
              <Link to='/tasks' className={linkClass('/tasks')}>
                Tasks
              </Link>
              <Link to='/models' className={linkClass('/models')}>
                Modelos
              </Link>
              <Link to='/settings' className={linkClass('/settings')}>
                Configuración
              </Link>
            </nav>
          </div>

          {/* User menu */}
          <div className='flex items-center space-x-4'>
            {/* Status indicator for backend auth */}
            {isAuthenticated && backendUser && (
              <div className='hidden sm:flex items-center space-x-2 text-sm text-gray-600'>
                <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                <span>
                  {backendUser.subscription_tier === 'premium' && (
                    <span className='text-amber-600 font-medium'>Premium</span>
                  )}
                  {backendUser.subscription_tier === 'free' && (
                    <span className='text-gray-500'>Free</span>
                  )}
                  {backendUser.subscription_tier === 'enterprise' && (
                    <span className='text-purple-600 font-medium'>
                      Enterprise
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Botón de actualizar */}
            <button
              onClick={dashboard.refreshStats}
              className='flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors'
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                />
              </svg>
              <span className='hidden sm:inline'>Actualizar</span>
            </button>

            {/* User info and menu */}
            {clerkUser && (
              <div className='flex items-center space-x-3'>
                {/* User info from backend */}
                {backendUser && (
                  <div className='hidden lg:block text-right'>
                    <div className='text-sm font-medium text-gray-900'>
                      {backendUser.full_name ||
                        backendUser.first_name ||
                        clerkUser.firstName}
                    </div>
                    <div className='text-xs text-gray-500'>
                      {backendUser.role} • {backendUser.login_count} logins
                    </div>
                  </div>
                )}

                <UserButton
                  afterSignOutUrl='/auth/sign-in'
                  appearance={{
                    elements: {
                      avatarBox: 'w-8 h-8',
                      userButtonPopoverCard: 'shadow-lg border border-gray-200',
                      userButtonPopoverActionButton: 'hover:bg-gray-50',
                    },
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
